import { useDispatch } from 'react-redux';

import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { disconnectNativeTelephonyAction, initNativeTelephonyAction } from 'store/system/actions';

/**
 * Native counterpart to WebTelephonyProvider. The web provider mounts the @telnyx/webrtc client and pipes
 * its connection + call events into Redux; on native the NativeCalling plugin owns the session, so this
 * provider binds the plugin's transport events to the same Redux actions, registers the always-on VoIP push
 * token, and holds a persistent SIP registration. Mounted only on native with the telephony claim (see
 * TelephonyHost).
 *
 * PERSISTENT SIP model (DEV-4332): initNativeTelephonyAction connects + logs the TxClient in ONCE on mount
 * and keeps it for the app lifetime, reconnecting only on a foreground resume if the OS dropped the socket.
 * Staying registered removes the connect/disconnect-vs-call race class the earlier lazy model suffered (the
 * idle-disconnect raced registration / the hangup BYE / a fresh connect). The SIP socket is NEVER
 * proactively disconnected on call end — only on logout/unmount (disconnectNativeTelephonyAction). The VoIP
 * push token (the doorbell) stays always-registered, independent of the SIP socket.
 */
const NativeTelephonyProvider = ({ children }) => {
  const dispatch = useDispatch();

  useEffectSafeStrictMode(() => {
    dispatch(initNativeTelephonyAction());

    return () => {
      dispatch(disconnectNativeTelephonyAction());
    };
  }, [dispatch]);

  return children;
};

NativeTelephonyProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default NativeTelephonyProvider;
